import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ApiGetSmsListHistoryOptions, filterKey, SmsScheduleType } from '@models/reseller/sms-history-detail';
import { ApiGetSubaccounts, Subaccount } from '@models/subaccount/subaccount';
import { User } from '@models/user';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { PlatformDomainService } from '@services/platform-domain.service';
import { SmsHistoryService } from '@services/sms-history.service';
import { SmsService } from '@services/sms.service';
import { SubaccountService } from '@services/subaccount.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { SmsDetailRenderComponent } from '@ui/components/atlas-grid/renderer/sms-detail-render/sms-detail-render.component';
import { creditsTypeIcon } from '@utils/constants';
import { formateDateTimeWithSeconds } from '@utils/utils';
import { Subject } from 'rxjs';
import { debounceTime, finalize, map } from 'rxjs/operators';
import { HistoryDetailModalComponent } from 'src/app/views/reseller/reseller-history/history-modal-detail/history-detail-modal.component';
import { LoaderService } from '@services/loader.service';

@Component({
  selector: 'wsb-sms-periodic',
  templateUrl: './sms-periodic.component.html',
  styleUrls: ['./sms-periodic.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class SmsPeriodicComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalExportSmsList') modalExportSmsList;
  @ViewChild('modalSuccessExport') modalSuccessExport;
  @ViewChild('modalExportError') modalExportError;
  @ViewChild('modalConfirmDelete') modalConfirmDelete;

  @Input() user: User;

  modalConfirmDeleteModal: NgbModalRef;
  modalExportErrorModal: NgbModalRef;
  modalSuccessExportModal: NgbModalRef;
  modalExportSmsListModal: NgbModalRef;

  fc: UntypedFormControl = new UntypedFormControl('', Validators.required);

  apiGetSmsListHistoryOptions = new ApiGetSmsListHistoryOptions();

  aggregableByCampain;
  linkAnalyticsPlaceholder;
  sendingPeriods;

  private search: Subject<string> = new Subject();
  gridApi;
  gridColumnApi;
  tableLoading: boolean;
  tableHistoryGridOptions = null;
  tableColumnsDefs: any = [];
  paginationPageSize = 10;
  tableHistoryData;
  components = {
    smsDetailRederer: SmsDetailRenderComponent,
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  scrollPageSubs = 1;
  subaccounts: Array<Subaccount> = [];

  filter;
  today = new Date();

  dateFrom;
  dateTo;
  credits: any[];
  typeLabel;
  idMessage;

  labels = [
    { id: 'SENDER', label: 'sender_sender' },
    { id: 'FREQUENCY', label: 'sms_detail_sendfreq' },
    { id: 'REPETITION', label: 'sms_detail_sendrepeats' },
    { id: 'INFINITE', label: 'sms_detail_sendrepeats_infinitive' },
    { id: 'SENDING-METHOD', label: 'sms_detail_sentfrom' }
  ];
  gridAnimationState = 'hide';

  constructor(
    private smsHistoryService: SmsHistoryService,
    private smsService: SmsService,
    private modalService: NgbModal,
    private platformDomainService: PlatformDomainService,
    private loaderService: LoaderService,
    private subaccountService: SubaccountService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.getPlatformDomainInfo();

    this.getCreditsType();

    this.translateLables();

    this.defineTable();

    this.gridOptionDefinition();
  }

  ngAfterViewInit(): void {
    this.grid.exportGlobal.subscribe(aggregableByCampain => {
      if (this.checkDateIntervalForExport()) {
        return this.openModalExportError();
      }
      this.aggregableByCampain = aggregableByCampain;
      this.exportCampaing();
    });
    this.grid.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  exportCampaing() {
    this.smsHistoryService
      .exportSendSmsList(this.fc.value, this.apiGetSmsListHistoryOptions, this.aggregableByCampain, this.user.profile.email)
      .subscribe(success => {
        this.modalService.dismissAll();
        this.openModalSuccessExport();
      });
  }

  checkDateIntervalForExport() {
    const dateFrom = new Date(this.grid.filtersModel[filterKey.fromDateKey]);
    const dateTo = new Date(this.grid.filtersModel[filterKey.toDateKey]);
    const diff = Math.round((dateFrom.getTime() - dateTo.getTime()) / (1000 * 60 * 60 * 24));
    return Math.abs(diff) > 30;
  }

  openModalSuccessExport(): void {
    this.modalSuccessExportModal = this.modalService.open(this.modalSuccessExport);
    this.modalSuccessExportModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  openModalExportError(): void {
    this.modalExportErrorModal = this.modalService.open(this.modalExportError);
    this.modalExportErrorModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  translateLables() {
    this.labels.forEach(label => {
      this.translateService.get(label.label).subscribe(translatedLabel => (label.label = translatedLabel));
    });
  }

  getCreditsType() {
    this.smsService.getMessageTypes().subscribe(data => {
      this.credits = data.data.map(x => {
        return {
          value: x.id,
          icon: creditsTypeIcon[x.id],
          title: x.label
        };
      });
    });
  }

  getSubaccountList() {
    this.search.pipe(debounceTime(250)).subscribe(filter => {
      const body: ApiGetSubaccounts = {
        login: filter
      } as ApiGetSubaccounts;

      this.subaccountService.getSubaccounts(null, null, null, null, body).subscribe(subs => {
        this.subaccounts = subs.data;
      });
    });
  }

  onSearch($event) {
    this.search.next($event.term);
  }

  private getPlatformDomainInfo() {
    this.platformDomainService
      .getPlatformDomainInfo()
      .pipe(
        map(data => {
          this.linkAnalyticsPlaceholder = data.richUrlPlaceholder;
          data.sendingPeriods.forEach(period => {
            period.descriptionLabel = 'send_sms_' + period.descriptionLabel;
          });
          return data;
        })
      )
      .subscribe(data => {
        this.sendingPeriods = data.sendingPeriods;
      });
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        // headerNameTranslate: 'common_title',
        // field: 'campaignName',
        // sortable: false,
        // onCellClicked: params => {
        //   this.openHistoryDetailModal(params.data.idMessage, params.data.scheduleType);
        // },
        // width: 200,
        // cellStyle: { cursor: 'pointer', 'text-decoration': 'underline', color: '#067337', 'font-weight': 'bold' }

        headerNameTranslate: 'common_title',
        field: 'campaignName',
        colPrefKey: 'campaignName',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const period = this.sendingPeriods.find(x => x.idPeriod === params.data.period['id-period']);

              let periodLabel;
              this.translateService.get(period.descriptionLabel).subscribe(translatedLabel => (periodLabel = translatedLabel));

              return (
                '<div class="d-flex title"> <div class="overflow-h"> <div class="campaignName title-overflow color-primary">' +
                params.data.campaignName +
                '</div> </div> <span > ' +
                this.labels[0].label +
                ': ' +
                params.data.sender +
                '</span> <span > ' +
                this.labels[1].label +
                ' ' +
                periodLabel +
                '</span> <span >' +
                this.labels[2].label +
                ' ' +
                (params.data.period.infinitive ? this.labels[3].label : params.data.period['num-repetitions']) +
                '</span> <span >' +
                this.labels[4].label +
                ': ' +
                params.data.sentFrom +
                '</span> </div>'
              );
            } else {
              return '';
            }
          }
        },

        sortable: false,
        onCellClicked: params => {
          this.openHistoryDetailModal(params.data.idMessage, params.data.scheduleType);
        },
        width: 180
      },
      {
        headerNameTranslate: 'common_text',
        field: 'message',
        colPrefKey: 'message',
        cellRenderer: 'htmlRenderer',
        width: 300,
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const label = this.translateService.instant('encrypted_label');
              return (
                '<div style="overflow: hidden;"> <div class="message-overflow">' +
                (params.data.message === 'xxx' ? label : params.data.message) +
                '</div></div>'
              );
            } else {
              return '';
            }
          }
        },
        sortable: false
      },
      {
        headerNameTranslate: 'common_status',
        field: 'status',
        sortable: false,
        colPrefKey: 'status',
        suppressCellFocus: true,
        width: 125,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              let badgeLabel;
              this.translateService.get('sms_detail_sendtype_2').subscribe(label => {
                badgeLabel = label;
              });
              return '<span class="badge badge-periodic">' + badgeLabel + '</span>';
            } else {
              return '';
            }
          }
        }
      },
      {
        headerNameTranslate: 'common_number_recipient',
        field: 'recipients',
        width: 110,
        sortable: false
      },
      {
        headerNameTranslate: 'common_data_added',
        field: 'createTime',
        sortable: false,
        width: 110,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const dateTime = formateDateTimeWithSeconds(params.data.createTime).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTime"><span>' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      // {
      //   headerNameTranslate: 'common_data_sent',
      //   field: 'dateForSend',
      //   cellRenderer: 'htmlRenderer',
      //   width: 110,
      //   sortable: false,
      //   sort: 'desc',
      //   cellRendererParams: {
      //     content: params => {
      //       if (params && params.data) {
      //         const dateTime = formateDateTimeWithSeconds(params.data.dateForSend).split(' ');
      //         const date = dateTime[0];
      //         const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
      //         return '<div class="dateTime"><span>' + date + '</span> <span>' + time + '</span></div>';
      //       }
      //       return '';
      //     }
      //   }
      // },
      {
        headerNameTranslate: 'common_type',
        field: 'idMessageType',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const icon = this.credits.filter(type => type.value === params.data.idMessageType);
              return '<div class="type"><img class="ml-2 info-icon" src="' + icon[0]?.icon + '" alt="" /> </div>';
            } else {
              return '';
            }
          }
        },
        sortable: false,
        width: 110
      },
      // {
      //   headerNameTranslate: 'sender_sender',
      //   field: 'sender',
      //   sortable: false
      // },
      // {
      //   headerNameTranslate: 'list_sms_tablefield_sentfrom',
      //   field: 'sentFrom',
      //   sortable: false
      // },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.openModalConfirmDelete(e.idMessage, e.scheduleType),
              label: 'common_delete'
            },
            {
              onClick: e => this.openHistoryDetailModal(e.idMessage, e.scheduleType),
              label: 'sms_campaigns_detail'
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  gridOptionDefinition() {
    const that = this;
    this.tableHistoryGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      // export: 'export',
      exportText: 'reseller_history_export_time_help',
      exportButtons: [{ label: 'campagin_sms_export', value: 'true' }],
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        message: ''
      },
      rowHeight: 140
    });
  }

  loadData() {
    this.grid.filtersModel[filterKey.period] = {
      label: 'timefilter_dates_thismonth',
      value: 'thismonth',
      translate: true
    };
    this.grid.refresh();
  }

  reloadList(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.smsHistoryService
      .getSmsScheduledList(
        SmsScheduleType.periodic,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? sort[0].colId : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  openHistoryDetailModal(idMessage, scheduleType) {
    const options: NgbModalOptions = {
      size: 'lg',
      windowClass: 'custom-history-detail-modal-periodic'
    };
    const landingPageModal = this.modalService.open(HistoryDetailModalComponent, options);
    (landingPageModal.componentInstance as HistoryDetailModalComponent).hideImport = true;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).idMessage = idMessage;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).hidden = true;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).scheduled = true;
    (landingPageModal.componentInstance as HistoryDetailModalComponent).scheduleType = scheduleType;
  }

  openModalConfirmDelete(idMessage, scheduledType): void {
    this.modalConfirmDeleteModal = this.modalService.open(this.modalConfirmDelete);
    this.modalConfirmDeleteModal.result.then(
      result => {
        this.smsHistoryService.deleteScheduledSms(idMessage, scheduledType).subscribe(() => {
          this.grid.refresh();
        });
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  close() {
    this.modalConfirmDeleteModal.close();
  }
}
